import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './CreateEstimate.scss';
import './CommonCss.scss';
import agent from '../../../agent';
import SendApproval from './SendApproval';
import { ExhibitCard, createEmptyExhibit } from './Exhibit';
import PhotoMarkupEditor from './PhotoMarkupEditor';
import { FaMapMarkedAlt } from 'react-icons/fa';

const List = Quill.import('formats/list');
const Inline = Quill.import('blots/inline');

// All supported list styles
const LIST_STYLES = {
    'decimal': { type: 'ordered', style: 'decimal' },
    'lower-alpha': { type: 'ordered', style: 'lower-alpha' },
    'upper-alpha': { type: 'ordered', style: 'upper-alpha' },
    'lower-roman': { type: 'ordered', style: 'lower-roman' },
    'upper-roman': { type: 'ordered', style: 'upper-roman' },
    'disc': { type: 'bullet', style: 'disc' },
    'circle': { type: 'bullet', style: 'circle' },
    'square': { type: 'bullet', style: 'square' },
    'arrow': { type: 'bullet', style: 'arrow' },
    'tick': { type: 'bullet', style: 'tick' }
};

class ExtendedList extends List {
    static create(value) {
        // Check if it's a custom list style
        if (LIST_STYLES[value]) {
            const config = LIST_STYLES[value];
            const node = super.create(config.type);
            node.setAttribute('data-list', value);
            node.style.listStyleType = config.style;
            return node;
        }
        return super.create(value);
    }

    static formats(domNode) {
        const domFormat = domNode.getAttribute('data-list');
        if (domFormat && LIST_STYLES[domFormat]) {
            const config = LIST_STYLES[domFormat];
            domNode.style.listStyleType = config.style;
            return domFormat;
        }
        return super.formats(domNode);
    }
}

// Custom Size format for pixel values
class CustomSize extends Inline {
    static blotName = 'size';
    static tagName = 'span';
    static create(value) {
        const node = super.create();
        if (value && typeof value === 'string' && value.endsWith('px')) {
            node.style.fontSize = value;
        }
        return node;
    }
    static formats(node) {
        const fontSize = node.style.fontSize;
        if (fontSize && typeof fontSize === 'string' && fontSize.endsWith('px')) {
            return fontSize;
        }
        return undefined;
    }
}

Quill.register(ExtendedList, true);
Quill.register(CustomSize, true);

// Key used in the combined items + exhibits order (ids of items and exhibits can overlap)
const keyOf = (type, id) => `${type}-${id}`;

const CreateEstimate = ({ onClose, currentUser, onSuccess, editEstimate = null }) => {
    const [formData, setFormData] = useState({
        lead: '',
        leadId: '',
        issueDate: new Date().toISOString().split('T')[0],
        expiryDate: '',
        quoteNumber: '',
        title: '',
        summary: '',
        disclaimer: `Please read the job scope and all the information in the estimate and addendum (hereinafter jointly "Estimate" thoroughly to make sure it meets your expectations. We pride ourselves on preparing very detailed and complete estimates so you do not have to make assumptions. If it's not in the Estimate, then it's not part of the scope of work and it will not be done. If there's missing information, omissions,and/or contradictions with what you expect and/or what you were told by the estimator or anyone else,please let us know and do NOT sign the Estimate.

        This Estimate, once signed by you, is a legal document. It's final, it's complete, and it's the only information and job scope you should rely on. Any revisions and modifications to the Estimate must be done ONLY in writing. You should not rely on any verbal statements or promises. If you do not understand any part of the Estimate, do not sign it until you have a full understanding of the Estimate and all the documents submitted to you.

        Unless clearly specified, this Estimate does not include removal of debris, soil, nor any object from the work area. Nor does it include patching, sanding, taping, painting, finishing work, or reinstallations of any kind (whether the objects, materials, and/or surfaces were initially removed by Zavza Seal or anyone else).

        If you need such services and they are not part of the scope of work, please let us know in advance so we can add them to the Estimate and ensure we send the right construction crew to your location. If additional work is needed during or after the construction, it will be priced separately.

        Signing this document finalizes this sale in accordance with the itemized project description. You may cancel this transaction any time prior to midnight three calendar days after you signed this Estimate. This Estimate is valid for 14 days and may be withdrawn at any time.`,
        warranty: `We hereby warrant that all labor and materials furnished and work performed by Zavza Seal LLC for {{customer_name}} at {{customer_address}}  will be free from defects due to defective materials or workmanship for a period of {{year_warranty}} from the date of completion. Should any defect develop in the materials furnished or work performed due to improper materials or workmanship, we shall fix the defect at no expense to the owner. Our liability shall only be limited to OUR WORK and OUR MATERIALS. This Warranty is only valid and applies to the specific surfaces we worked on and should not be interpreted as a warranty for other areas or other surfaces we did not work on. Our liability shall be limited to the cost of fixing the defect, but not to any incidental or related damages as a result of the defect. Nothing in the above shall be deemed to apply to work that has been abused or neglected by the owner or other people.`,
        warrantyYear: '',
        addressImage: null,
        terms: '',
        categoryId: '',
        estimationNumber: '',
    });
    const [items, setItems] = useState([
    { id: 1, item: 'item 1', description: '', qty: '1', price: '', account: 'Services', taxRate: 'Tax Exempt', amount: '', galleryImages: [], isValid: true }
    ]);
    const [emailSettings, setEmailSettings] = useState({
        approval_send_to: '',
        approval_cc: '',
        client_bcc: '',
        approval_subject_template: '',
        client_subject_template: ''
    });
    useEffect(() => {
    const loadSettings = async () => {
        try {
            const res = await agent.Auth.getSettings();
            setPlatform(res?.data?.settings?.accounting_platform);
            setEmailSettings({
                approval_send_to: res?.data?.settings?.approval_send_to || '',
                approval_cc:      res?.data?.settings?.approval_cc      || '',
                client_bcc:       res?.data?.settings?.client_bcc       || '',
                approval_subject_template: res?.data?.settings?.approval_subject_template || '',
                client_subject_template:   res?.data?.settings?.client_subject_template   || '',
            });
        } catch (err) {
            console.error("Settings fetch error:", err);
        }
    };
    loadSettings();
    }, []);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    // PDF and Xero loading states
    const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
    const [isSendingToXero, setIsSendingToXero] = useState(false);
    const [isSendingToQuickbook, setIsSendingToQuickbook] = useState(false);
    // Lead search states
    const [leadSearch, setLeadSearch] = useState('');
    const [leadSuggestions, setLeadSuggestions] = useState([]);
    const [showLeadDropdown, setShowLeadDropdown] = useState(false);
    const [isSearchingLeads, setIsSearchingLeads] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [leadSearchTimeout, setLeadSearchTimeout] = useState(null);
    // Gallery photo being edited: { itemId, imageId, source, name }
    const [photoEditor, setPhotoEditor] = useState(null);
    // Warranty uses the same rich editor as line items (client: bold an added name, Oct 2026)
    const warrantyQuillRef = useRef(null);
    const [disclaimerCursorPosition, setDisclaimerCursorPosition] = useState(0);
    // Template-related states
    const [templates, setTemplates] = useState([]);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [templateSearch, setTemplateSearch] = useState('');
    const [templateSearchTimeout, setTemplateSearchTimeout] = useState(null);
    // Edit mode states
    const [isEditMode, setIsEditMode] = useState(!!editEstimate);
    const [loadingEstimate, setLoadingEstimate] = useState(false);
    const [editEstimateNumber, setEditEstimateNumber] = useState('');
    const [editXeroQuoteNumber, setEditXeroQuoteNumber] = useState('');
    const [editqboQuoteNumber, setEditQboQuoteNumber] = useState('');
    const [viewPDF, setViewPDF] = useState(false);
    const [activityLogs, setActivityLogs] = useState([]);
    const [loadingActivity, setLoadingActivity] = useState(false);
    const [showActivity, setShowActivity] = useState(true);
    // Send for approval states
    const [showSendApproval, setShowSendApproval] = useState(false);
    const [isSendingApproval, setIsSendingApproval] = useState(false);
    // Send to client states
    const [showSendToClient, setShowSendToClient] = useState(false);
    const [isSendingToClient, setIsSendingToClient] = useState(false);
    const [clientEmail, setClientEmail] = useState('');
    const [draggedIndex, setDraggedIndex] = useState(null);
    // Row the dragged block is currently over (shows a blue drop line)
    const [dragOverIndex, setDragOverIndex] = useState(null);
    // A row can only be dragged after grabbing its ⠿ handle (so text selection in editors doesn't drag the row)
    const [dragReadyIndex, setDragReadyIndex] = useState(null);
    const [savedCustomerName, setSavedCustomerName] = useState('');
    const [savedEstimateNumber, setSavedEstimateNumber] = useState('');


    // Exhibits state (exhibits now live inside the items table, so start with none)
    const [exhibits, setExhibits] = useState([]);

    // Combined order of items and exhibits, e.g. ['item-1', 'exhibit-1', 'item-2']
    const [layout, setLayout] = useState([]);

    // Items and exhibits merged into one list, in the order shown on screen
    const orderedBlocks = useMemo(() => {
        const blockMap = new Map();
        items.forEach(item => blockMap.set(keyOf('item', item.id), { type: 'item', data: item }));
        exhibits.forEach(exhibit => blockMap.set(keyOf('exhibit', exhibit.id), { type: 'exhibit', data: exhibit }));

        const result = [];
        layout.forEach(key => {
            if (blockMap.has(key)) {
                result.push({ key, ...blockMap.get(key) });
                blockMap.delete(key);
            }
        });
        // Newly added items / exhibits go to the bottom
        blockMap.forEach((block, key) => result.push({ key, ...block }));
        return result;
    }, [items, exhibits, layout]);

    // Exhibit letters (A, B, C...) follow their position in the list
    const exhibitLetters = {};
    orderedBlocks
        .filter(block => block.type === 'exhibit')
        .forEach((block, index) => { exhibitLetters[block.data.id] = String.fromCharCode(65 + index); });

    const reorderBlocks = (fromIndex, toIndex) => {
        if (fromIndex === toIndex || toIndex < 0 || toIndex >= orderedBlocks.length) return;
        const keys = orderedBlocks.map(block => block.key);
        const [moved] = keys.splice(fromIndex, 1);
        keys.splice(toIndex, 0, moved);
        setLayout(keys);
    };

    const addExhibit = () => {
        const maxId = exhibits.length > 0 ? Math.max(...exhibits.map(exhibit => Number(exhibit.id) || 0)) : 0;
        setExhibits(prev => [...prev, createEmptyExhibit(maxId + 1)]);
    };


    const draftKey = editEstimate?.id ? `estimate_draft_${editEstimate.id}` : 'estimate_draft_new';
    const [pendingDraft, setPendingDraft] = useState(null);   // draft found, waiting for user decision
    const [lastAutoSaved, setLastAutoSaved] = useState(null);
    const [autoSaveError, setAutoSaveError] = useState(null);  // ✅ NEW: e.g. browser storage full
    const [submitError, setSubmitError] = useState(null);      // ✅ NEW: why Create / Update failed
    const draftChecked = useRef(false);
    const draftBaseline = useRef(null);                        // ✅ NEW: form state when the draft check ran

    useEffect(() => {
      if (loadingEstimate) return;
      draftChecked.current = false;
      draftBaseline.current = null;   // ✅ NEW
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) setPendingDraft(JSON.parse(raw));
      } catch (e) {
        console.error('Draft read error:', e);
      }
      draftChecked.current = true;
    }, [draftKey, loadingEstimate]);


    useEffect(() => {
      // ✅ CHANGED: "pendingDraft" removed here. Before, nothing was auto-saved while the
      // "Unsaved draft found" banner was showing, so a refresh lost all new work.
      if (!draftChecked.current || loadingEstimate) return;

      // ✅ NEW: while the banner is shown, save only after the user really changes something
      // (edit mode loads the estimate first; that alone must not replace the old draft)
      const snapshot = JSON.stringify([formData, items, exhibits, layout, leadSearch, selectedLead]);
      if (draftBaseline.current === null) draftBaseline.current = snapshot;

      // Don't save an untouched, empty form
      const hasContent =
        formData.lead ||
        items.some(i => (i.description && i.description !== '<p><br></p>') || i.price) ||
        exhibits.some(e => e.name || e.textareas.some(t => t.content));
      if (!hasContent) return;
      if (pendingDraft && snapshot === draftBaseline.current) return;   // ✅ NEW

      const timer = setTimeout(() => {
        try {
          const draft = {
            savedAt: Date.now(),
            formData: {
              ...formData,
              // File objects can't be serialized
              addressImage: typeof formData.addressImage === 'string' ? formData.addressImage : null,
            },
            // keep only images already on the server; new File uploads can't be stored
            items: items.map(i => ({ ...i, galleryImages: i.galleryImages.filter(g => g.isExisting) })),
            exhibits: exhibits.map(e => ({ ...e, images: e.images.filter(g => g.isExisting) })),
            layout,
            leadSearch,
            selectedLead,
          };
          // ✅ NEW: user started new work instead of restoring → keep the old draft as a
          // backup copy, hide the banner; the new work becomes the draft
          if (pendingDraft) {
            try { localStorage.setItem(draftKey + '_previous', JSON.stringify(pendingDraft)); } catch (e) { /* no room: skip */ }
            setPendingDraft(null);
          }
          localStorage.setItem(draftKey, JSON.stringify(draft));
          setLastAutoSaved(new Date());
          setAutoSaveError(null);   // ✅ NEW
        } catch (e) {
          console.error('Autosave failed:', e);
          // ✅ NEW: tell the user (usually browser storage is full)
          setAutoSaveError('Auto-save failed — your browser storage is full. Click "' + (editEstimate?.id ? 'Update' : 'Create') + ' Estimate" to save your work.');
        }
      }, 1500);

      return () => clearTimeout(timer);
    }, [formData, items, exhibits, layout, leadSearch, selectedLead, pendingDraft, loadingEstimate, draftKey]);


    const restoreDraft = () => {
      if (!pendingDraft) return;
      setFormData(prev => ({ ...prev, ...pendingDraft.formData }));
      setItems(pendingDraft.items);
      setExhibits(pendingDraft.exhibits || []);
      setLayout(pendingDraft.layout || []);
      setLeadSearch(pendingDraft.leadSearch || '');
      setSelectedLead(pendingDraft.selectedLead || null);
      setPendingDraft(null);
    };

    const discardDraft = () => {
      localStorage.removeItem(draftKey);
      setPendingDraft(null);
    };



    // The modal scrolls inside one of the parent elements, not the window, so find it
    const getScrollParent = (node) => {
      let el = node;
      while (el && el !== document.body) {
        const { overflowY } = window.getComputedStyle(el);
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
          return el;
        }
        el = el.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    };

   const autoScrollLoop = useCallback(() => {
      const scroller = scrollParentRef.current;
      if (dragScrollY.current !== null && scroller) {
        const threshold = 120;   // px from the visible edge that triggers scrolling
        const maxSpeed = 25;     // px per frame at the very edge
        const isPage = scroller === document.scrollingElement || scroller === document.documentElement;
        const rect = isPage ? { top: 0, bottom: window.innerHeight } : scroller.getBoundingClientRect();
        const top = Math.max(rect.top, 0);
        const bottom = Math.min(rect.bottom, window.innerHeight);
        const y = dragScrollY.current;
        let delta = 0;

        if (y < top + threshold) {
          delta = -Math.ceil(((top + threshold - y) / threshold) * maxSpeed);
        } else if (y > bottom - threshold) {
          delta = Math.ceil(((y - (bottom - threshold)) / threshold) * maxSpeed);
        }

        if (delta !== 0) {
          scroller.scrollTop += delta;
        }
      }
      autoScrollRAF.current = requestAnimationFrame(autoScrollLoop);
    }, []);

    // Tracks the cursor anywhere on the page (also over the sticky header / outside the table)
    const trackDragPosition = useCallback((e) => {
      dragScrollY.current = e.clientY;
    }, []);

    const stopAutoScroll = useCallback(() => {
      dragScrollY.current = null;
      document.removeEventListener('dragover', trackDragPosition);
      if (autoScrollRAF.current) {
        cancelAnimationFrame(autoScrollRAF.current);
        autoScrollRAF.current = null;
      }
    }, [trackDragPosition]);

    // Small label used as the drag preview instead of the whole (very tall) row
    const setSmallDragImage = (e, index) => {
      const block = orderedBlocks[index];
      let label = 'Line item';
      if (block?.type === 'exhibit') {
        label = `Exhibit ${exhibitLetters[block.data.id] || ''}${block.data.name ? ' – ' + block.data.name : ''}`;
      } else if (block?.type === 'item') {
        const text = (block.data.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        label = text ? `Line item – ${text.substring(0, 40)}${text.length > 40 ? '…' : ''}` : 'Line item';
      }

      const ghost = document.createElement('div');
      ghost.textContent = '⠿ ' + label;
      ghost.style.cssText = 'position:fixed;top:-1000px;left:-1000px;padding:8px 14px;background:#28a8e2;color:#fff;border-radius:6px;font:600 13px Arial,sans-serif;white-space:nowrap;box-shadow:0 4px 12px rgba(0,0,0,0.2);';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 12, 12);
      setTimeout(() => ghost.remove(), 0);
    };

    const handleDragStart = (e, index) => {
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index)); // Firefox needs data to start a drag
      setSmallDragImage(e, index);

      scrollParentRef.current = getScrollParent(scrollContainerRef.current);
      dragScrollY.current = e.clientY;
      document.addEventListener('dragover', trackDragPosition);
      autoScrollRAF.current = requestAnimationFrame(autoScrollLoop);
    };

    const handleDragOver = (e, index) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      dragScrollY.current = e.clientY; // track cursor position for auto-scroll
      if (dragOverIndex !== index) setDragOverIndex(index);
    };

    const handleDrop = (e, dropIndex) => {
      e.preventDefault();
      if (draggedIndex === null) return;
      reorderBlocks(draggedIndex, dropIndex);
      setDraggedIndex(null);
      setDragOverIndex(null);
    };

    const handleDragEnd = () => {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setDragReadyIndex(null);
      stopAutoScroll();
    };

    // Stop auto-scroll if the component closes in the middle of a drag
    useEffect(() => stopAutoScroll, [stopAutoScroll]);

    // Moves an item or exhibit one position up (-1) or down (+1)
    const moveItem = (index, direction) => reorderBlocks(index, index + direction);

    // Release the drag handle if the mouse is let go without dragging
    useEffect(() => {
      const releaseHandle = () => setDragReadyIndex(null);
      window.addEventListener('mouseup', releaseHandle);
      return () => window.removeEventListener('mouseup', releaseHandle);
    }, []);

    // Refs for ReactQuill components
    const quillRefs = useRef({});
    const scrollContainerRef = useRef(null);
    const dragScrollY = useRef(null);
    const autoScrollRAF = useRef(null);
    const scrollParentRef = useRef(null);
    const [platform, setPlatform] = useState(null);
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const res = await agent.Auth.getSettings();
                setPlatform(res?.data?.settings?.accounting_platform);
            } catch (err) {
                console.error("Settings fetch error:", err);
            }
        };
        loadSettings();
    }, []);

    const fetchCategories = async () => {
        setLoadingCategories(true);
        try {
            const response = await agent.categories.getCategoriesData('', 1, 100);
            if (response && response.isSuccess && response.data && response.data.categories) {
                setCategories(response.data.categories.data || []);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        } finally {
            setLoadingCategories(false);
        }
    };
    useEffect(() => {
        fetchCategories();
    }, []);

    const accountOptions = [
        { value: 'Sales', label: 'Sales' },
        { value: 'Services', label: 'Services' },
        { value: 'Products', label: 'Products' }
    ];
    const taxRateOptions = [
        { value: 'Tax Exempt', label: 'Tax Exempt' },
        { value: 'Tax on Sales', label: 'Tax on Sales' },
        { value: 'Tax on Purchases', label: 'Tax on Purchases' }
    ];
    const warrantyYearOptions = [
        { value: '', label: '— Select Warranty —' },
        { value: '0', label: 'No warranty' },
        { value: '5', label: 'Five (5) years' },
        { value: '10', label: 'Ten (10) years' },
        { value: '20', label: 'Twenty (20) years' },
        { value: '30', label: 'Thirty (30) years' },
        { value: '40', label: 'Forty (40) years' }
    ];
    const warrantyVariables = [
        { key: '{{customer_name}}', label: 'Customer Name', description: 'Insert customer name' },
        { key: '{{customer_address}}', label: 'Customer Address', description: 'Insert customer address' },
        { key: '{{year_warranty}}', label: 'Warranty Years', description: 'Insert warranty period' },
    ];
    // Quill editor configuration (matching CreateTemplatePage)
    const quillModules = useMemo(() => ({
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ size: ['2px', '3px', '5px', '6px', '7px', '8px', '9px', '10px', '11px', '12px', '13px', '14px', '15px', '16px', '17px', '18px', '19px', '20px', '21px', '22px', '23px', '24px', '25px', '26px', '27px', '28px', '29px', '30px', '31px', '32px', '33px', '34px', '35px', '36px', '37px', '38px', '39px', '40px'] }],
                // [{ list: 'ordered' }, { list: 'bullet' }, 'listStyle'],
                ['listStyle'],
                [{ align: [] }],
                [
                { color: [
                    '#000000', '#e60000', '#ff9900', '#ffff00', '#008a00', '#0066cc', '#9933ff',
                    '#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff',
                    '#bbbbbb', '#f06666', '#ffc266', '#ffff66', '#66b966', '#66a3e0', '#c285ff',
                    '#888888', '#a10000', '#b26b00', '#b2b200', '#006100', '#0047b2', '#6b24b2',
                    '#444444', '#5c0000', '#663d00', '#666600', '#003700', '#002966', '#3d1466'
                ] },
                { background: [] }
            ],
                ['clean']
            ],
            handlers: {
                listStyle() {
                    const quill = this.quill;
                    const currentFormat = quill.getFormat();
                    // Map standard list types to our custom styles
                    let currentListStyle = currentFormat.list;
                    if (currentListStyle === 'ordered') {
                        currentListStyle = 'decimal';
                    } else if (currentListStyle === 'bullet') {
                        currentListStyle = 'disc';
                    } else if (!currentListStyle) {
                        currentListStyle = null;
                    }
                    // Create dropdown menu
                    const toolbar = quill.getModule('toolbar');
                    const container = toolbar.container;
                    const button = container.querySelector('.ql-listStyle');
                    if (!button) return;
                    // Remove existing dropdown if present
                    const existingDropdown = document.querySelector('.quill-list-style-dropdown');
                    if (existingDropdown) {
                        existingDropdown.remove();
                        return;
                    }
                    // Create dropdown
                    const dropdown = document.createElement('div');
                    dropdown.className = 'quill-list-style-dropdown';
                    // Close dropdown when clicking outside
                    const closeDropdown = (e) => {
                        if (!dropdown.contains(e.target) && e.target !== button && !button.contains(e.target)) {
                            dropdown.remove();
                            document.removeEventListener('click', closeDropdown);
                        }
                    };
                    const orderedStyles = [
                        { value: 'decimal', label: '1. 2. 3.', name: 'Decimal' },
                        { value: 'lower-alpha', label: 'a. b. c.', name: 'Lower Alpha' },
                        { value: 'upper-alpha', label: 'A. B. C.', name: 'Upper Alpha' },
                        { value: 'lower-roman', label: 'i. ii. iii.', name: 'Lower Roman' },
                        { value: 'upper-roman', label: 'I. II. III.', name: 'Upper Roman' }
                    ];
                    const unorderedStyles = [
                        { value: 'disc', label: '• • •', name: 'Disc' },
                        { value: 'circle', label: '○ ○ ○', name: 'Circle' },
                        { value: 'square', label: '■ ■ ■', name: 'Square' },
                        { value: 'arrow', label: '→ → →', name: 'Arrow' },
                        { value: 'tick', label: '✓ ✓ ✓', name: 'Tick' }
                    ];
                    // Add ordered list section
                    const orderedHeader = document.createElement('div');
                    orderedHeader.className = 'quill-list-style-header';
                    orderedHeader.textContent = 'Ordered Lists';
                    dropdown.appendChild(orderedHeader);
                    orderedStyles.forEach(style => {
                        const option = document.createElement('div');
                        option.className = 'quill-list-style-option';
                        if (currentListStyle === style.value) {
                            option.classList.add('active');
                        }
                        const label = document.createElement('span');
                        label.className = 'quill-list-style-label';
                        label.textContent = style.label;

                        const name = document.createElement('span');
                        name.className = 'quill-list-style-name';
                        name.textContent = style.name;

                        option.appendChild(label);
                        option.appendChild(name);

                        option.addEventListener('click', (e) => {
                            e.stopPropagation();
                            const isActive = currentListStyle === style.value;
                            quill.format('list', isActive ? false : style.value);
                            dropdown.remove();
                            document.removeEventListener('click', closeDropdown);
                        });

                        dropdown.appendChild(option);
                    });

                    // Add separator
                    const separator = document.createElement('div');
                    separator.className = 'quill-list-style-separator';
                    dropdown.appendChild(separator);

                    // Add unordered list section
                    const unorderedHeader = document.createElement('div');
                    unorderedHeader.className = 'quill-list-style-header';
                    unorderedHeader.textContent = 'Unordered Lists';
                    dropdown.appendChild(unorderedHeader);

                    unorderedStyles.forEach(style => {
                        const option = document.createElement('div');
                        option.className = 'quill-list-style-option';
                        if (currentListStyle === style.value) {
                            option.classList.add('active');
                        }

                        const label = document.createElement('span');
                        label.className = 'quill-list-style-label';
                        label.textContent = style.label;

                        const name = document.createElement('span');
                        name.className = 'quill-list-style-name';
                        name.textContent = style.name;

                        option.appendChild(label);
                        option.appendChild(name);

                        option.addEventListener('click', (e) => {
                            e.stopPropagation();
                            const isActive = currentListStyle === style.value;
                            quill.format('list', isActive ? false : style.value);
                            dropdown.remove();
                            document.removeEventListener('click', closeDropdown);
                        });

                        dropdown.appendChild(option);
                    });

                    // Position dropdown
                    const buttonRect = button.getBoundingClientRect();
                    dropdown.style.position = 'fixed';
                    dropdown.style.top = `${buttonRect.bottom + 5}px`;
                    dropdown.style.left = `${buttonRect.left}px`;
                    dropdown.style.zIndex = '10000';

                    document.body.appendChild(dropdown);

                    setTimeout(() => {
                        document.addEventListener('click', closeDropdown);
                    }, 0);
                }
            }
        },
        clipboard: {
        matchVisual: false,
        //matchers: [
           // [Node.ELEMENT_NODE, (node, delta) => {
               // delta.ops.forEach(op => {
                   // if (op.attributes) {
                       // delete op.attributes.color;
                       // delete op.attributes.background;
                    //}
               // });
               // return delta;
          //  }]
       // ]
    }


    }), []);

    const quillFormats = [
        'header', 'font', 'size',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'script',
        'blockquote', 'code-block',
        'list', 'bullet', 'indent',
        'direction', 'align',
        'link', 'image', 'video'
    ];

    // Custom ReactQuill component with stable refs to prevent re-renders
    const QuillEditor = React.memo(({ itemId, value, onChange, placeholder }) => {
            const quillRef = useRef(null);
            const isUpdatingRef = useRef(false);
            const lastValueRef = useRef(value);

            useEffect(() => {
                if (quillRef.current) {
                    quillRefs.current[itemId] = quillRef.current;
                }
            }, [itemId]);

            const handleQuillChange = useCallback((content) => {
                // Prevent infinite loops and unnecessary updates
                if (isUpdatingRef.current || lastValueRef.current === content) return;

                lastValueRef.current = content;
                onChange(content);
            }, [onChange]);

            // Only update editor content when value changes externally (not from typing)
            useEffect(() => {
                if (quillRef.current && quillRef.current.getEditor && value !== lastValueRef.current) {
                const editor = quillRef.current.getEditor();
                if (editor) {
                    const currentContent = editor.root.innerHTML;
                        if (currentContent !== (value || '')) {
                            isUpdatingRef.current = true;
                            editor.root.innerHTML = value || '';
                            lastValueRef.current = value;
                            isUpdatingRef.current = false;
                        }
                    }
                }
            }, [value]);

            return (
                <div className="quill-editor-container">
                    <ReactQuill ref={quillRef} theme="snow" value={value || ''} onChange={handleQuillChange} modules={quillModules} formats={quillFormats} placeholder={placeholder} readOnly={false} />
                </div>
            );
        }, (prevProps, nextProps) => {
        // Custom comparison to prevent unnecessary re-renders
        return (
            prevProps.itemId === nextProps.itemId &&
            prevProps.value === nextProps.value &&
            prevProps.placeholder === nextProps.placeholder
        );
    });

    // State for API error handling
    const [apiError, setApiError] = useState(null);
    const [estimateApiError, setEstimateApiError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);


     const formatActivityDate = (dateString) => {
        if (!dateString) return '--';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    const formatSectionLabel = (section) => {
        const labels = {
            header: 'Estimate details',
            items: 'Line items',
            exhibits: 'Exhibits',
            all: 'All sections',
        };
        return labels[section] || section;
    };

    const formatFieldLabel = (field) => {
        const labels = {
            lead: 'Lead',
            lead_id: 'Lead ID',
            title: 'Title',
            summary: 'Summary',
            disclaimer: 'Disclaimer',
            warranty: 'Warranty',
            warranty_year: 'Warranty year',
            terms: 'Terms',
            category_id: 'Category',
            estimation_number: 'Estimate number',
            totals: 'Totals',
            address_image: 'Address image',
            item: 'Item',
            description: 'Description',
            qty: 'Qty',
            price: 'Price',
            amount: 'Amount',
            discount: 'Discount',
            account: 'Account',
            taxRate: 'Tax rate',
            gallery_images: 'Gallery images',
            name: 'Exhibit name',
            textareas: 'Text areas',
            sort_order: 'Position',
        };
        return labels[field] || field.replace(/_/g, ' ');
    };

    const getActivitySummary = (log) => {
        if (!log) return '';
        if (log.action === 'create') return 'Created the estimate';
        if (log.action === 'delete') return 'Deleted the estimate';

        const sections = Array.isArray(log.changed_sections) ? log.changed_sections : [];
        if (sections.length === 0) {
            return log.changes?.message || 'Saved with no field changes';
        }

        const parts = [];
        if (sections.includes('header') && log.changes?.header) {
            const fields = Object.keys(log.changes.header).map(formatFieldLabel);
            parts.push(`Details (${fields.join(', ')})`);
        }
        if (sections.includes('items') && log.changes?.items) {
            const itemCount = (log.changes.items.changes || []).length;
            parts.push(`Line items (${itemCount} change${itemCount === 1 ? '' : 's'})`);
        }
        if (sections.includes('exhibits') && log.changes?.exhibits) {
            const exhibitCount = (log.changes.exhibits.changes || []).length;
            parts.push(`Exhibits (${exhibitCount} change${exhibitCount === 1 ? '' : 's'})`);
        }
        if (parts.length === 0) {
            return `Updated ${sections.map(formatSectionLabel).join(', ')}`;
        }
        return `Updated ${parts.join('; ')}`;
    };

    const loadActivityLogs = async (estimateId) => {
        if (!estimateId) return;
        setLoadingActivity(true);
        try {
            const response = await agent.estimates.getEstimateActivityLogs(estimateId);
            if (response && response.isSuccess && response.data) {
                setActivityLogs(response.data.activityLogs || []);
            } else {
                setActivityLogs([]);
            }
        } catch (error) {
            console.error('Error loading estimate activity logs:', error);
            setActivityLogs([]);
        } finally {
            setLoadingActivity(false);
        }
    };

    // Load estimate data for editing
    const loadEstimateData = async (estimateId) => {
        if (!estimateId) return;
        setLoadingEstimate(true);
        setApiError(null);
        loadActivityLogs(estimateId);
        try {
            const response = await agent.estimates.getEstimateById(estimateId);
            if (response && response.isSuccess && response.data && response.data.estimate) {
                const estimate = response.data.estimate;

                // Populate form data
                setFormData(prev => ({
                    ...prev,
                    lead: estimate.lead || '',
                    leadId: estimate.leadId || estimate.lead_id || '',
                    issueDate: estimate.issue_date ? estimate.issue_date.split('T')[0] : new Date().toISOString().split('T')[0],
                    expiryDate: estimate.expiry_date ? estimate.expiry_date.split('T')[0] : '',
                    quoteNumber: estimate.quote_number || '',
                    title: estimate.title || '',
                    summary: estimate.summary || '',
                    // disclaimer: estimate.disclaimer || prev.disclaimer,
                    // warranty: estimate.warranty || prev.warranty,
                    disclaimer: estimate.disclaimer || '',
                    warranty: estimate.warranty || '',
                    warrantyYear: estimate.warranty_year || '0',
                    addressImage: estimate?.address_image?.path ? process.env.REACT_APP_BACKEND + estimate.address_image.path : null,
                    terms: estimate.terms || '',
                    categoryId: estimate.category_id ? String(estimate.category_id) : '',
                    estimationNumber:estimate.estimation_number
                }));

                if(estimate.pdf_path && estimate.pdf_path !== ""){
                    setViewPDF(true);
                }
                if(estimate.lead_data && estimate.lead_data.lead_json_data && estimate.lead_data.lead_json_data.EMAIL && Array.isArray(estimate.lead_data.lead_json_data.EMAIL) && estimate.lead_data.lead_json_data.EMAIL.length > 0) {
                        setClientEmail(estimate.lead_data.lead_json_data.EMAIL[0].VALUE);
                }

                // Create subtitle: First Name - Last Name - Address
                const firstName = estimate.lead_data?.NAME || '';
                const lastName = estimate.lead_data?.LAST_NAME || '';
                let customerName = `${firstName} ${lastName}`.trim();
                if (!customerName) customerName = estimate.lead_data?.lead_json_data?.TITLE || '';
                setSavedCustomerName(customerName);
                setSavedEstimateNumber(estimate.estimation_number || '');
                const lead_name  = firstName + ' ' + lastName + ' #' + estimate.lead_id;
                const estimate_name  = firstName + ' ' + lastName + ' ' + estimate.estimation_number;

                setEditEstimateNumber(estimate_name);
                if (estimate.quote_number) {
                    setEditXeroQuoteNumber('XERO QUOTE #' + estimate.quote_number);
                }
                if (estimate.qb_doc_number) {
                    setEditQboQuoteNumber('QBO NO. #' + estimate.qb_doc_number);
                }

                // Set lead search state
                setLeadSearch(lead_name);
                if (estimate.lead) {
                    const leadJson = estimate.lead_data?.lead_json_data || {};
                    setSelectedLead({
                        id: estimate.leadId || estimate.lead_id || 'existing',
                        leadId: estimate.leadId || estimate.lead_id || '',
                        name: estimate.lead,
                        address: leadJson.UF_CRM_LEAD_1708606658714 || leadJson.ADDRESS || '',
                        subtitle: '',
                        email: '',
                        phone: ''
                    });
                }

                // Populate items if they exist
                let mappedItems = [];
                if (estimate.items && Array.isArray(estimate.items)) {
                    mappedItems = estimate.items.map((item, index) => ({
                        id: item.id || index + 1,
                        item: item.item || '',
                        description: item.description || '',
                        qty: item.qty || '',
                        price: item.price || '',
                        account: item.account || 'Services',
                        taxRate: item.taxRate || 'Tax Exempt',
                        amount: item.amount || '',
                        galleryImages: item.gallery_images ? item.gallery_images.map((img, index) => ({
                        id: `existing_${item.id}_${index}`, // Unique ID for existing images
                        name: img.filename,
                        type: img.type,
                        size: img.size,
                        path: process.env.REACT_APP_BACKEND + img.path,
                        preview: process.env.REACT_APP_BACKEND + img.path,
                        isExisting: true, // Flag to identify existing images
                        originalPath: img.path, // Store original path for API calls
                        file: null // No file object for existing images
                    })) : [],
                    isValid: validateItem({ item: item.item || '' })
                }));
                setItems(mappedItems);
            }

            let mappedExhibitItems = [];
            if (estimate.exhibitItems && Array.isArray(estimate.exhibitItems) && estimate.exhibitItems.length > 0) {
                    mappedExhibitItems = estimate.exhibitItems.map((exhibitItem, index) => ({
                            id: exhibitItem.id || index + 1,
                            name: exhibitItem.name || '',
                            images: exhibitItem.gallery_images ? exhibitItem.gallery_images.map((img, index) => ({
                            id: `existing_${exhibitItem.id}_${index}`, // Unique ID for existing images
                            name: img.filename,
                            type: img.type,
                            size: img.size,
                            path: process.env.REACT_APP_BACKEND + img.path,
                            preview: process.env.REACT_APP_BACKEND + img.path,
                            isExisting: true, // Flag to identify existing images
                            originalPath: img.path, // Store original path for API calls
                            file: null // No file object for existing images
                        })) : [],
                        textareas: exhibitItem.textareas ? exhibitItem.textareas.map((textarea, index) => ({
                            id: `existing_${exhibitItem.id}_${index}`, // Unique ID for existing images
                            content: textarea.content,
                            title: textarea.title,
                            isExisting: true // Flag to identify existing images
                        })) : [],
                        isValid: validateItem({ item: exhibitItem.item || '' })
                    }));
                }
                setExhibits(mappedExhibitItems);

                // Rebuild the combined order. Old estimates have no sort_order:
                // items keep their order and exhibits go to the end (same as before).
                const orderedKeys = [
                    ...mappedItems.map((item, index) => ({
                        key: keyOf('item', item.id),
                        order: estimate.items[index].sort_order ?? index,
                    })),
                    ...mappedExhibitItems.map((exhibit, index) => ({
                        key: keyOf('exhibit', exhibit.id),
                        order: estimate.exhibitItems[index].sort_order ?? 100000 + index,
                    })),
                ]
                    .sort((a, b) => a.order - b.order)
                    .map(block => block.key);
                setLayout(orderedKeys);

            } else {
                setApiError('Failed to load estimate data');
            }
        } catch (error) {
            console.error('Error loading estimate:', error);
            setApiError('Failed to load estimate data. Please try again.');
        } finally {
            setLoadingEstimate(false);
        }
    };

    // Template functions
    const fetchTemplates = async (searchQuery = '') => {
        setLoadingTemplates(true);
        try {
            const response = await agent.estimates.getEstimateTemplates(searchQuery);
            if (response && response.isSuccess && response.data && response.data.templates) {
                setTemplates(response.data.templates);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleImportTemplate = (itemId) => {
        setSelectedItemId(itemId);
        setShowTemplateModal(true);
        setTemplateSearch(''); // Reset search when opening modal
        fetchTemplates(); // Always fetch templates when opening modal
    };

    const handleTemplateSelect = (template) => {
        if (selectedItemId) {
            setItems(prev => prev.map(item => {
                if (item.id === selectedItemId) {
                    return {
                        ...item,
                        description: template.template_content || ''
                    };
                }
                return item;
            }));
        }
        setShowTemplateModal(false);
        setSelectedItemId(null);
    };

    const closeTemplateModal = () => {
        setShowTemplateModal(false);
        setSelectedItemId(null);
        setTemplateSearch(''); // Clear search when closing modal
    };

    // Template search functionality
    const handleTemplateSearch = (value) => {
        setTemplateSearch(value);

        // Clear existing timeout
        if (templateSearchTimeout) {
            clearTimeout(templateSearchTimeout);
        }

        // Search templates with debounce
        const timeoutId = setTimeout(() => {
            fetchTemplates(value);
        }, 300);

        setTemplateSearchTimeout(timeoutId);
    };

    // Lead search functionality
    const searchLeads = async (query) => {
        if (!query || query.length < 2) {
            setLeadSuggestions([]);
            setShowLeadDropdown(false);
            return;
        }

        if (!currentUser || !currentUser.id) {
            console.error('No current user found');
            setApiError('User not authenticated');
            return;
        }

        setIsSearchingLeads(true);
        setApiError(null);

    try {
      const response = await agent.estimates.getLeadsBySearch(query);

      if (response && response.isSuccess && response.data && response.data.leads && Array.isArray(response.data.leads)) {
        // Map the API response to match our expected format
        const mappedLeads = response.data.leads.map(lead => {
          // Extract address from lead_json_data
          let address = '';
          if (lead.lead_json_data && lead.lead_json_data.UF_CRM_LEAD_1708606658714) {
            address = lead.lead_json_data.UF_CRM_LEAD_1708606658714;
          }else if (lead.lead_json_data && lead.lead_json_data.ADDRESS) {
            address = lead.lead_json_data.ADDRESS;
          }

          // Create subtitle: First Name - Last Name - Address
          const firstName = lead.NAME || '';
          const lastName = lead.LAST_NAME || '';
          const name  = firstName + ' ' + lastName + ' #' + lead.lead_id;

          return {
            id: lead.id,
            leadId: lead.lead_id, // Use lead_id if available, fallback to id
            name: name,
            subtitle: address || 'No address',
            address: address,
            email: '', // Not available in this data structure
            phone: lead.phone || (lead.lead_json_data && lead.lead_json_data.PHONE && lead.lead_json_data.PHONE[0] ? lead.lead_json_data.PHONE[0].VALUE : ''),
            leadData: lead // Store full lead data for reference
          };
        });

        //console.log(JSON.stringify(mappedLeads, null, 2));

        setLeadSuggestions(mappedLeads);
        setShowLeadDropdown(true);
      } else {
        setLeadSuggestions([]);
        setShowLeadDropdown(true);
      }
    } catch (error) {
      console.error('Error searching leads:', error);
      setApiError('Failed to search leads. Please try again.');
      setLeadSuggestions([]);
      setShowLeadDropdown(true);
    } finally {
      setIsSearchingLeads(false);
    }
  };

    const handleLeadSearch = (value) => {
        setLeadSearch(value);
        setFormData(prev => ({
            ...prev,
            lead: value
        }));

        // Clear selected lead if user is typing
        if (selectedLead) {
            setSelectedLead(null);
        }

        // Search leads with debounce
        if (leadSearchTimeout) {
            clearTimeout(leadSearchTimeout);
        }

        const timeoutId = setTimeout(() => {
            searchLeads(value);
        }, 300);
        setLeadSearchTimeout(timeoutId);
    };

    const handleLeadSelect = (lead) => {
        setErrors({});
        if(lead && lead.address === ""){
            setSelectedLead(null);
            setLeadSearch('');
            setFormData(prev => ({
                ...prev,
                lead: '',
                leadId: '',
            }));
            setShowLeadDropdown(false);
            setLeadSuggestions([]);
            setErrors(prev => ({
                ...prev,
                lead: 'Warning: This lead has no address. Please add an address before proceeding.'
            }));
            return;
        }
        setSelectedLead(lead);
        setLeadSearch(lead.name);
        setFormData(prev => ({
            ...prev,
            lead: lead.name,
            leadId: lead.leadId
        }));
        setShowLeadDropdown(false);
        setLeadSuggestions([]);
    };

    const handleCreateNewLead = () => {
        if (leadSearch.trim()) {
            // Create new lead with the search term
            const newLead = {
                id: 'new',
                leadId: '',
                name: leadSearch.trim(),
                subtitle: '',
                email: '',
                phone: ''
            };
            handleLeadSelect(newLead);
        }
    };

    const handleClearLead = () => {
        setSelectedLead(null);
        setLeadSearch('');
        setFormData(prev => ({
            ...prev,
            lead: '',
            leadId: '',
        }));
        setShowLeadDropdown(false);
        setLeadSuggestions([]);
        setApiError(null);
    };

    const handleAddressImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                addressImage: file
            }));
        }
    };

    // Old warranties are plain text: show each line as a paragraph in the editor (empty line = empty paragraph)
    const warrantyEditorHtml = (text) => {
        const value = text || '';

        if (/<(p|br|b|strong|em|u|s|span|ol|ul|li)[\s>\/]/i.test(value)) {
            return value;
        }

        const escape = (line) => line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        return value
            .replace(/\r\n?/g, '\n')
            .split('\n')
            .map((line) => (line.trim() === '' ? '<p><br></p>' : `<p>${escape(line)}</p>`))
            .join('');
    };

    const handleWarrantyEditorChange = (content) => {
        handleInputChange('warranty', content);
    };

    const handleInsertVariable = (variable) => {
        const quill = warrantyQuillRef.current && warrantyQuillRef.current.getEditor ? warrantyQuillRef.current.getEditor() : null;

        if (!quill) {
            return;
        }

        // Insert at the cursor (or at the end when the editor had no focus yet)
        const range = quill.getSelection(true);
        const index = range ? range.index : Math.max(0, quill.getLength() - 1);

        quill.insertText(index, variable.key, 'user');
        quill.setSelection(index + variable.key.length, 0, 'user');
    };
    const handleDisclaimerTextChange = (e) => {
      setDisclaimerCursorPosition(e.target.selectionStart);
      handleInputChange('disclaimer', e.target.value);
    };

    const handleDisclaimerCursorChange = (e) => {
      setDisclaimerCursorPosition(e.target.selectionStart);
    };

    const handleInsertDisclaimerVariable = (variable) => {
      const currentDisclaimer = formData.disclaimer;
      const cursorPos = disclaimerCursorPosition;
      const newDisclaimer = currentDisclaimer.substring(0, cursorPos) + variable.key + currentDisclaimer.substring(cursorPos);

      setFormData(prev => ({
        ...prev,
        disclaimer: newDisclaimer
      }));

      setDisclaimerCursorPosition(cursorPos + variable.key.length);

      setTimeout(() => {
        const textarea = document.getElementById('disclaimer-textarea');
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(cursorPos + variable.key.length, cursorPos + variable.key.length);
        }
      }, 0);
    };



  const handleDownloadPDF = async () => {
    if (!editEstimate || !editEstimate.id) {
      alert('Please save the estimate first before downloading PDF');
      return;
    }

    try {
      setIsDownloadingPDF(true);

      // Use agent API to download PDF
      const response = await agent.estimates.downloadEstimatePDF(editEstimate.id);

      // Create a blob from the response and trigger download
      if (response && response.isSuccess && response.data?.outputPath) {

        setSuccessMessage('PDF successfully generated!');
        setEstimateApiError(null);
        await loadEstimateData(editEstimate.id);
        // const blob = new Blob([response.data.outputPath], { type: 'application/pdf' });
        // const url = window.URL.createObjectURL(blob);
        // const link = document.createElement('a');
        // link.href = url;
        // link.download = `estimate_${editEstimate.id}_${Date.now()}.pdf`;
        // document.body.appendChild(link);
        // link.click();
        // document.body.removeChild(link);
        // window.URL.revokeObjectURL(url);
      }

    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const handleViewPDF = async () => {
    if (!editEstimate || !editEstimate.id) {
      alert('Please save the estimate first before viewing PDF');
      return;
    }

    try {
      setIsDownloadingPDF(true);

      // Use agent API to get PDF
      const response = await agent.estimates.viewEstimatePDF(editEstimate.id);

      // Create a blob from the response and open in new tab
      if (response && response.data?.pdf && response.data.pdf !== "") {
        window.open(response.data.pdf, '_blank');
      }

    } catch (error) {
      console.error('Error viewing PDF:', error);
      alert('Failed to view PDF. Please try again.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const handleSendToXero = async () => {
    if (!editEstimate || !editEstimate.id) {
      alert('Please save the estimate first before sending to Xero');
      return;
    }

    try {
      setIsSendingToXero(true);
      const response = await agent.estimates.sendEstimateToXero(editEstimate.id);

     // console.log('✅ Response:', JSON.stringify(response, null, 2));

      if (response &&  response.data && response.data.authUrl) {
        window.location.href = response.data.authUrl;
      }else if (response && response?.data?.quote?.QuoteID) {
        // Successfully sent to Xero, refresh estimate data
        await loadEstimateData(editEstimate.id);
        setSuccessMessage('Estimate successfully sent to Xero!');
        setEstimateApiError(null);
      } else {
        setEstimateApiError('Xero integration failed: No Quote ID received.');
        setSuccessMessage(null);
      }
    } catch (error) {
      console.error('Error sending to Xero:', error);
      setEstimateApiError(error.message || 'Xero integration failed.');
      setSuccessMessage(null);
    } finally {
      setIsSendingToXero(false);
    }
  };


  const handleSendToQuickbook = async () => {
    if (!editEstimate || !editEstimate.id) {
      alert('Please save the estimate first before sending to Quickbook');
      return;
    }

    try {
      setIsSendingToQuickbook(true);
      const response = await agent.estimates.sendEstimateToQuickbooks(editEstimate.id);

      if (response &&  response.data && response.data.authUrl) {
        window.location.href = response.data.authUrl;
      }else if (response && response?.data?.data?.Estimate) {

        await loadEstimateData(editEstimate.id);
        setSuccessMessage('Estimate successfully sent to QuickBooks!');
        setEstimateApiError(null);
      } else {
        setEstimateApiError('QuickBooks integration failed');
        setSuccessMessage(null);
      }
    } catch (error) {
      console.error('Error sending to QuickBooks:', error);
      setEstimateApiError(error.message || 'QuickBooks integration failed.');
      setSuccessMessage(null);
    } finally {
      setIsSendingToQuickbook(false);
    }
  };

  // Click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLeadDropdown && !event.target.closest('.create-estimate-lead-wrapper')) {
        setShowLeadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLeadDropdown]);

  // Suppress findDOMNode warnings from third-party libraries
  useEffect(() => {
    const originalWarn = console.warn;
    console.warn = (...args) => {
      if (args[0] && args[0].includes && args[0].includes('findDOMNode is deprecated')) {
        return; // Suppress this specific warning
      }
      originalWarn.apply(console, args);
    };

    return () => {
      console.warn = originalWarn;
    };
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (leadSearchTimeout) {
        clearTimeout(leadSearchTimeout);
      }
      if (templateSearchTimeout) {
        clearTimeout(templateSearchTimeout);
      }
    };
  }, [leadSearchTimeout, templateSearchTimeout]);

  // Reset edit mode when editEstimate prop changes
  useEffect(() => {
    if (editEstimate && editEstimate.id) {
      setIsEditMode(true);
      loadEstimateData(editEstimate.id);
    } else {
      setIsEditMode(false);
      setActivityLogs([]);
      // Reset form data to default when switching to create mode
      setFormData({
        lead: '',
        leadId: '',
        issueDate: new Date().toISOString().split('T')[0],
        expiryDate: '',
        quoteNumber: '',
        title: '',
        summary: '',
        disclaimer: `Please read the job scope and all the information in the addendum and estimate (jointly the “Estimate”) thoroughly to ensure it meets your expectations. We pride ourselves on preparing very detailed and complete estimates so you do not have to make assumptions. If it’s not in the Estimate, then it’s not part of the scope of work and it will not be done. If you don't understand the Estimate, or find missing information, omissions, or contradictions with what you expect or what the estimator or anyone else told you, do NOT sign the Estimate until you fully understand it. Once you receive the invoice and pay a deposit, the Estimate becomes a legal document – It's final, complete, and the only information and job scope you should rely on. Any revisions or modifications to the Estimate must be made only in writing. Do not rely on any verbal statements or promises.

Unless clearly specified, this Estimate does not include removal of debris, soil, or any objects from the work area. Unless clearly specified, this Estimate excludes patching, taping, sanding painting, finishing work, or any reinstallation (whether the objects, materials, and/or surfaces were initially removed by Zavza Seal or anyone else). If a project requires a permit, the property owner is responsible for obtaining it. This Estimate excludes all permit and permit-related costs. These costs will be paid to government agencies and other third parties outside our control.

If additional work is needed during or after construction, we will price it separately. You may cancel this transaction any time prior to midnight three (3) calendar days after you signed this Estimate. This Estimate is valid for seven (7) calendar days and may be withdrawn at any time.`,
        warranty: `We hereby warrant that all labor and materials furnished and work performed by Zavza Seal LLC for {{customer_name}} at {{customer_address}} will be free from defects due to defective materials or workmanship for a period of {{year_warranty}} from the date of completion. Should any defect develop in the materials furnished or work performed due to improper materials or workmanship, we shall fix the defect at no expense to the owner. Our liability shall only be limited to OUR WORK and OUR MATERIALS. This Warranty is only valid and applies to the specific surfaces we worked on and should not be interpreted as a warranty for other areas or other surfaces we did not work on. Our liability shall be limited to the cost of fixing the defect, but not to any incidental or related damages as a result of the defect. Nothing in the above shall be deemed to apply to work that has been abused or neglected by the owner or other people.

This Warranty excludes any fixtures, equipment, doors, windows, and pumps we have installed, as those are not manufactured or produced by us. These are warranted by their respective manufacturers and they normally have a lower warranty time.`,
        warrantyYear: '',
        addressImage: null,
        terms: '',
        categoryId: ''
      });
      setItems([
        { id: 1, item: 'item 1', description: '', qty: '1', price: '', account: 'Services', taxRate: 'Tax Exempt', amount: '', galleryImages: [], isValid: true }
      ]);
      setExhibits([]);
      setLayout([]);
      setLeadSearch('');
      setSelectedLead(null);
      setApiError(null);
      setTemplateSearch(''); // Reset template search
    }
  }, [editEstimate]);

  // Cleanup gallery images and exhibit images on unmount
  useEffect(() => {
    return () => {
      items.forEach(item => {
        if (item.galleryImages) {
          item.galleryImages.forEach(image => {
            // Only revoke URLs for new images (not existing ones)
            if (!image.isExisting && image.preview) {
              URL.revokeObjectURL(image.preview);
            }
          });
        }
      });

      exhibits.forEach(exhibit => {
        if (exhibit.images) {
          exhibit.images.forEach(image => {
            // Only revoke URLs for new images (not existing ones)
            if (!image.isExisting && image.preview) {
              URL.revokeObjectURL(image.preview);
            }
          });
        }
      });
    };
  }, []);

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-dismiss estimate API error after 8 seconds
  useEffect(() => {
    if (estimateApiError) {
      const timer = setTimeout(() => {
        setEstimateApiError(null);
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [estimateApiError]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear API error when user makes changes
    if (apiError) {
      setApiError(null);
    }

    // Clear estimate API error when user makes changes
    if (estimateApiError) {
      setEstimateApiError(null);
    }
  };

  const handleItemChange = useCallback((itemId, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        // Only update if the value actually changed
        if (item[field] === value) {
          return item;
        }

        const updatedItem = { ...item, [field]: value };

        // Calculate amount if qty and price are provided
        if (field === 'qty' || field === 'price') {
          const qty = field === 'qty' ? (parseFloat(value) || (value === '' ? 1 : 0)) : (parseFloat(item.qty) || (item.qty === '' ? 1 : 0));
          const price = field === 'price' ? parseFloat(value) || 0 : parseFloat(item.price) || 0;

          // If price is negative, treat it as a discount
          const amount = qty * price;

          updatedItem.amount = amount.toFixed(2);
        }

        // Validate item
        updatedItem.isValid = validateItem(updatedItem);

        return updatedItem;
      }
      return item;
    }));
  }, []);

  // Separate handler for editor changes to avoid interference
  const handleItemEditorChange = useCallback((itemId, value) => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        // Only update if the value actually changed
        if (item.description === value) {
          return item;
        }

        const updatedItem = { ...item, description: value };

        // Validate item
        updatedItem.isValid = validateItem(updatedItem);

        return updatedItem;
      }
      return item;
    }));
  }, []);

  // Stable editor change handler that doesn't cause re-renders
  const handleEditorChange = useCallback((itemId) => {
    return (value) => {
      // Direct update without causing re-render of the editor
      setItems(prev => prev.map(item => {
        if (item.id === itemId) {
          // Only update if the value actually changed
          if (item.description === value) {
            return item;
          }

          const updatedItem = { ...item, description: value };

          // Validate item
          updatedItem.isValid = validateItem(updatedItem);

          return updatedItem;
        }
        return item;
      }));
    };
  }, []);

  // Insert "----newBox----" + newline into description editor at cursor (or end)
  const handleInsertNewBox = useCallback((itemId) => {
    const rq = quillRefs.current[itemId];
    if (!rq || !rq.getEditor) return;
    const quill = rq.getEditor();
    const index = quill.getLength(); // always insert at end
    const text = '----newBox----\n\n'; // newline after marker, then blank line for new text
    quill.insertText(index, text);
    quill.setSelection(index + text.length);
  }, []);

  const handleGalleryImageAdd = (itemId, files) => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const newImages = Array.from(files).map((file, index) => ({
          id: `new_${itemId}_${Date.now()}_${index}`, // Unique ID for new images
          file: file,
          preview: URL.createObjectURL(file),
          name: file.name,
          type: file.type,
          size: file.size,
          path: null, // No path for new images
          isExisting: false, // Flag to identify new images
          originalPath: null // No original path for new images
        }));

        return {
          ...item,
          galleryImages: [...item.galleryImages, ...newImages]
        };
      }
      return item;
    }));
  };

  // ✅ NEW: change the photo order (drag a photo onto another, or ◀ ▶)
  const galleryDrag = useRef(null); // { itemId, index } of the photo being dragged
  const moveGalleryImage = (itemId, fromIndex, toIndex) => {
    setItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      if (fromIndex === toIndex || toIndex < 0 || toIndex >= item.galleryImages.length) return item;
      const images = [...item.galleryImages];
      const [moved] = images.splice(fromIndex, 1);
      images.splice(toIndex, 0, moved);
      return { ...item, galleryImages: images };
    }));
  };

  const handleGalleryImageRemove = (itemId, imageId) => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const imageToRemove = item.galleryImages.find(img => img.id === imageId);
        if (imageToRemove) {
          // Only revoke URL for new images (File objects)
          if (!imageToRemove.isExisting && imageToRemove.preview) {
            URL.revokeObjectURL(imageToRemove.preview);
          }
        }

        return {
          ...item,
          galleryImages: item.galleryImages.filter(img => img.id !== imageId)
        };
      }
      return item;
    }));
  };

  // Open the photo editor at once (loading screen), then load the photo.
  // target: { type: 'gallery', itemId, imageId } or { type: 'address' }; photo: { file, url, name }
  const openPhotoEditor = async (target, photo) => {
    setPhotoEditor({ target, source: null, name: photo.name, ownUrl: false });
    try {
      let blob = photo.file;
      if (!blob) {
        const response = await fetch(photo.url);
        if (!response.ok) throw new Error('load failed');
        blob = await response.blob();
      }
      setPhotoEditor({ target, source: URL.createObjectURL(blob), name: photo.name, ownUrl: true });
    } catch (error) {
      setPhotoEditor(null);
      alert('This photo could not be opened for editing. Please remove it and upload it again.');
    }
  };

  const closePhotoEditor = () => {
    if (photoEditor && photoEditor.ownUrl && photoEditor.source) {
      URL.revokeObjectURL(photoEditor.source);
    }
    setPhotoEditor(null);
  };

  // The edited photo replaces the original in the same place and is uploaded as a new photo on save
  const handlePhotoEdited = (file) => {
    if (!photoEditor) return;
    if (photoEditor.target.type === 'address') {
      handleInputChange('addressImage', file);
      closePhotoEditor();
      return;
    }
    const { itemId, imageId } = photoEditor.target;
    setItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      return {
        ...item,
        galleryImages: item.galleryImages.map(img => {
          if (img.id !== imageId) return img;
          if (!img.isExisting && img.preview) {
            URL.revokeObjectURL(img.preview);
          }
          return {
            id: `new_${itemId}_${Date.now()}_edited`,
            file: file,
            preview: URL.createObjectURL(file),
            name: file.name,
            type: file.type,
            size: file.size,
            path: null,
            isExisting: false,
            originalPath: null
          };
        })
      };
    }));
    closePhotoEditor();
  };

  const validateItem = (item) => {
    // No validation required - item name is optional
    return true;
  };

  const addItem = () => {
    const maxId = items.length > 0 ? Math.max(...items.map(item => item.id)) : 0;
    const newId = maxId + 1;
    setItems(prev => [...prev, {
      id: newId,
      item: '',
      description: '',
      qty: '1',
      price: '',
      account: '',
      taxRate: '',
      amount: '',
      galleryImages: [],
      isValid: true
    }]);
  };

  const removeItem = (itemId) => {
    setItems(prev => {
      const itemToRemove = prev.find(item => item.id === itemId);
      if (itemToRemove && itemToRemove.galleryImages) {
        // Clean up object URLs for new images only to prevent memory leaks
        itemToRemove.galleryImages.forEach(image => {
          if (!image.isExisting && image.preview) {
            URL.revokeObjectURL(image.preview);
          }
        });
      }
      return prev.filter(item => item.id !== itemId);
    });
  };

  const calculateTotals = () => {
    // Calculate subtotal including all prices (positive and negative)
    const subtotal_discounted = items.reduce((sum, item) => {
      const qty = parseFloat(item.qty) || (item.qty === '' ? 1 : 0);
      const price = parseFloat(item.price) || 0;
      return sum + (qty * price);
    }, 0);

    // Calculate total discount from negative prices (for display purposes)
    const totalDiscount = items.reduce((sum, item) => {
      const qty = parseFloat(item.qty) || (item.qty === '' ? 1 : 0);
      const price = parseFloat(item.price) || 0;
      if (price < 0) {
        return sum + Math.abs(qty * price); // Convert negative to positive for discount display
      }
      return sum;
    }, 0);

    // Total is the same as subtotal since discounts are already included in negative prices
    const total = subtotal_discounted;

    const subtotal = subtotal_discounted + totalDiscount;

    return {
      subtotal: subtotal.toFixed(2),
      discount: totalDiscount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const getInvalidItemsCount = () => {
    return items.filter(item => !item.isValid).length;
  };

  // ✅ NEW: readable reason for a failed Create / Update
  // ✅ CHANGED: readable reason + a short technical detail (shown in grey, helps us find the cause)
  // upload = { count, bytes } of the photo files sent with this save
  const describeSubmitError = (error, response, upload = { count: 0, bytes: 0 }) => {
    const status = error?.status || error?.response?.status;
    const serverMessage = response?.message || error?.response?.body?.message;
    const megabytes = (upload.bytes / 1048576).toFixed(1);
    const photos = upload.count > 0 ? `${upload.count} photo${upload.count === 1 ? '' : 's'}, about ${megabytes} MB` : 'no new photos';

    let detail = status ? `HTTP ${status}` : (error ? 'no response from server' : 'server reported a failure');
    if (error?.code) detail += ` · ${error.code}`;
    detail += ` · ${photos}`;

    let text;
    if (status === 413) {
      text = `The photos are too large to upload together (${photos}). Remove some photos (or use smaller ones) and try again.`;
    } else if (status === 401 || status === 403) {
      text = 'Your login has expired. Copy your work, log in again, and retry.';
    } else if (status === 504 || status === 502 || error?.timeout) {
      text = 'The server took too long to answer (large photos?). Please try again in a moment.';
    } else if (serverMessage) {
      text = serverMessage;
    } else if (error && !status && upload.count > 0) {
      // The server closed the connection without an answer — with photos attached this is almost
      // always the upload size limit (the browser can't see the real reason).
      text = `The upload didn't finish — the photos may be too large to send together (${photos}). Try again with fewer photos: save first, then add the rest and click Update.`;
    } else if (error && !status) {
      text = 'Could not reach the server. Check your internet connection and try again.';
    } else {
      text = 'Could not save the estimate. Please try again.';
    }

    return { text, detail };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);   // ✅ NEW
    let uploadInfo = { count: 0, bytes: 0 };   // ✅ NEW: photo files in this save (for the error message)

    try {

        const newErrors = {};

        if (!formData.lead.trim()) {
          newErrors.lead = 'Lead is required';
        } else if (!formData.leadId) {
          newErrors.lead = 'Please choose a lead from the dropdown list';
        }

        if (formData.warrantyYear === '') {
          newErrors.warrantyYear = 'Warranty period is required';
        }

        if (!formData.categoryId) {
          newErrors.categoryId = 'Category is required';
        }

        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
          scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          setIsSubmitting(false);
          return;
        }

      // Prepare estimate data for API using FormData
      const formDataToSend = new FormData();

      // Add basic form data
      Object.keys(formData).forEach(key => {
        if (key !== 'addressImage' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add address image if exists
      if (formData.addressImage) {
        formDataToSend.append('addressImage', formData.addressImage);
      }

      // Add totals
      const totals = calculateTotals();
      formDataToSend.append('totals', JSON.stringify(totals));

      // Position of every item / exhibit in the combined list (saved as sort_order)
      const sortOrder = {};
      orderedBlocks.forEach((block, index) => { sortOrder[block.key] = index; });

      // Add items data (item name is optional, so include all items), in screen order
      const validItems = orderedBlocks.filter(block => block.type === 'item').map(block => block.data);
      const itemsData = validItems.map((item, itemIndex) => {
        // ✅ CHANGED: the photos in screen order — saved photos as they are, new photos as a
        // placeholder naming their uploaded file (sent below), so the server keeps this order.
        let newIndex = 0;
        const orderedImages = item.galleryImages.map(img => (img.isExisting
          ? img
          : { isNew: true, fileField: `galleryImages_${itemIndex}_${newIndex++}` }));

        return {
          ...item,
          galleryImages: orderedImages,
          sortOrder: sortOrder[keyOf('item', item.id)]
        };
      });
      formDataToSend.append('items', JSON.stringify(itemsData));

      // Add new gallery images as separate files (index must match itemsData)
      validItems.forEach((item, itemIndex) => {
        const newImages = item.galleryImages.filter(img => !img.isExisting);
        newImages.forEach((image, imageIndex) => {
          if (image.file) {
            formDataToSend.append(`galleryImages_${itemIndex}_${imageIndex}`, image.file);
          }
        });
      });

      // Add exhibits data, in screen order
      const validExhibits = orderedBlocks
        .filter(block => block.type === 'exhibit')
        .map(block => block.data)
        .filter(exhibit =>
          exhibit.images.length > 0 ||
          exhibit.textareas.some(textarea => (textarea.content || '').trim() !== '') ||
          (exhibit.name && exhibit.name.trim() !== '')
        );

      if (validExhibits.length > 0) {
        const exhibitsData = validExhibits.map((exhibit, exhibitIndex) => {
          // ✅ CHANGED: photos in screen order (saved photos + placeholders for new uploads)
          let newIndex = 0;
          const orderedImages = exhibit.images.map(img => (img.isExisting
            ? img
            : { isNew: true, fileField: `exhibitImages_${exhibitIndex}_${newIndex++}` }));

          return {
            id: exhibit.id,
            name: exhibit.name ? exhibit.name.trim() : '',
            images: orderedImages,
            sortOrder: sortOrder[keyOf('exhibit', exhibit.id)],
            textareas: exhibit.textareas
              .filter(textarea => (textarea.content || '').trim() !== '')
              .map(textarea => ({
                id: textarea.id,
                title: textarea.title || '',
                content: textarea.content
              }))
          };
        });

        formDataToSend.append('exhibits', JSON.stringify(exhibitsData));

        // Add new exhibit images as separate files (index must match exhibitsData)
        validExhibits.forEach((exhibit, exhibitIndex) => {
          const newImages = exhibit.images.filter(img => !img.isExisting);
          newImages.forEach((image, imageIndex) => {
            if (image.file) {
              formDataToSend.append(`exhibitImages_${exhibitIndex}_${imageIndex}`, image.file);
            }
          });
        });
      }

      console.log(isEditMode ? 'Updating estimate with FormData...' : 'Creating estimate with FormData...');

      // ✅ NEW: how many photo files (and MB) this save uploads
      for (const [, value] of formDataToSend.entries()) {
        if (value instanceof File) {
          uploadInfo.count += 1;
          uploadInfo.bytes += value.size || 0;
        }
      }


      // Send to API via agent
      let response;
      if (isEditMode && editEstimate && editEstimate.id) {
        // Update existing estimate
        formDataToSend.append('id', editEstimate.id);
        response = await agent.estimates.updateEstimate(editEstimate.id, formDataToSend);
      } else {
        // Create new estimate
        response = await agent.estimates.createEstimate(formDataToSend);
      }

      if (response && response.isSuccess) {

        console.log(isEditMode ? 'Estimate updated successfully:' : 'Estimate created successfully:', response.data);

        localStorage.removeItem(draftKey);

        if (onSuccess) {
          onSuccess();
        }
        // Close modal on success
        onClose();

       } else {
        console.error(isEditMode ? 'Failed to update estimate:' : 'Failed to create estimate:', response?.message || 'Unknown error');
        // ✅ CHANGED: show the error (own message, not apiError: in edit mode apiError replaces the whole form)
        setSubmitError(describeSubmitError(null, response, uploadInfo));
      }

    } catch (error) {
      console.error('Error creating estimate:', error);
      setSubmitError(describeSubmitError(error, null, uploadInfo));   // ✅ CHANGED
    } finally {
      setIsSubmitting(false);
    }
  };

  const totals = calculateTotals();
  const invalidItemsCount = getInvalidItemsCount();

  // Drag handle + up/down buttons, shared by item rows and exhibit rows
  const renderHandleCell = (index, extra = null) => (
    <td className="text-center align-middle" style={{ width: '32px', maxWidth: '32px', padding: '4px 2px' }}>
      <div className="d-flex flex-column align-items-center gap-0" style={{ lineHeight: 1 }}>
        <span
          title="Drag to reorder"
          style={{ fontSize: '14px', opacity: 0.5, cursor: 'grab' }}
          onMouseDown={() => setDragReadyIndex(index)}
        >
          ⠿
        </span>
        <button
          type="button"
          className="btn btn-sm p-0 border-0 bg-transparent"
          style={{ fontSize: '10px', lineHeight: 1 }}
          onClick={() => moveItem(index, -1)}
          disabled={index === 0}
          title="Move up"
        >
          ▲
        </button>
        <button
          type="button"
          className="btn btn-sm p-0 border-0 bg-transparent"
          style={{ fontSize: '10px', lineHeight: 1 }}
          onClick={() => moveItem(index, 1)}
          disabled={index === orderedBlocks.length - 1}
          title="Move down"
        >
          ▼
        </button>
      </div>
      {extra}
    </td>
  );

    const DEFAULT_SUBJECT = 'Estimate from Zavza Seal LLC - {{customer_name}} - {{estimate_number}}';

    const resolveSubject = (template) => {
      const vars = {
        customer_name: savedCustomerName,
        estimate_number: savedEstimateNumber,
        category: categories.find(c => String(c.id) === String(formData.categoryId))?.name || '',
      };
      return (template || DEFAULT_SUBJECT)
        .replace(/{{\s*(\w+)\s*}}/g, (_, k) => vars[k] || '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const hasUnsavedNumber = isEditMode && formData.estimationNumber !== savedEstimateNumber;


  // Show loading state while fetching estimate data
  if (loadingEstimate) {
    return (
      <div className="create-estimate" >
        <div className="create-estimate-header d-flex justify-content-between align-items-center px-4 py-2 border-bottom bg-light sticky-top me-0 flex-wrap">
          <h2 className="m-0 fs-6 fw-bold text-black">Edit Estimate</h2>
          <button className="create-estimate-close modal-closebtn bg-transparent border-0 fs-5 p-2 rounded-1 text-black text-opacity-50" onClick={onClose}>✕</button>
        </div>
        <div className="create-estimate-loading-container">
          <div className="create-estimate-loading">
            <span className="create-estimate-loading">⟳</span>
            <span>Loading estimate data...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show error state for edit mode when API fails
  if (isEditMode && apiError) {
    return (
      <div className="create-estimate">
        <div className="create-estimate-header d-flex justify-content-between align-items-center px-4 py-2 border-bottom bg-light sticky-top me-0 flex-wrap">
          <h2 className="m-0 fs-6 fw-bold text-black">Edit Estimate</h2>
          <button className="create-estimate-close modal-closebtn bg-transparent border-0 fs-5 p-2 rounded-1 text-black text-opacity-50" onClick={onClose}>✕</button>
        </div>
        <div className="create-estimate-error-container">
          <div className="create-estimate-error-content">
            <span className="error-icon">⚠️</span>
            <h3>Failed to Load Estimate</h3>
            <p>{apiError}</p>
            <div className="create-estimate-error-actions">
              <button
                type="button"
                className="create-estimate-retry-btn"
                onClick={() => editEstimate && loadEstimateData(editEstimate.id)}
              >
                Retry Loading
              </button>
              <button
                type="button"
                className="create-estimate-cancel-btn"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-estimate" ref={scrollContainerRef}>
      <div className="create-estimate-header d-flex justify-content-between align-items-center px-4 py-2 border-bottom bg-light sticky-top me-0 flex-wrap">
        {isEditMode ? (
          <div className="edit-estimate-title d-flex align-items-center gap-2 fw-bold justify-content-center fs-6">
            <span className="edit-text text-black">Edit Estimate</span>
            <span className="separator fw-normal text-black">|</span>
            <span className="customer-info text-black">
              {editEstimateNumber}
            </span>
            {editXeroQuoteNumber && (
              <>
                <span className="separator fw-normal text-black">|</span>
                <span className="xero-quote text-black">{editXeroQuoteNumber}</span>
              </>
            )}

            {editqboQuoteNumber && (
              <>
                <span className="separator fw-normal text-black">|</span>
                <span className="xero-quote text-black">{editqboQuoteNumber}</span>
              </>
            )}


          </div>
        ) : (
          <h2 className="m-0 fs-6 fw-bold text-black">Create Estimate</h2>
        )}
        <button className="create-estimate-close modal-closebtn bg-transparent border-0 fs-5 p-2 rounded-1 text-black text-opacity-50" onClick={onClose}>✕</button>
      </div>

      <div className="create-estimate-header-actions d-flex align-items-center justify-content-end gap-3 py-3 px-4 border-bottom">
        {isEditMode && (
            <>
              {viewPDF && (
                <button type="button" className="create-estimate-btns create-estimate-download-pdf" onClick={handleViewPDF} disabled={isSubmitting || loadingEstimate || isDownloadingPDF || isSendingToXero} title="View PDF in new tab">
                  {isDownloadingPDF ? (
                    <>
                      <span className="create-estimate-loading">⟳</span>
                      Loading PDF...
                    </>
                  ) : (
                    '👁️ View PDF'
                  )}
                </button>
              )}
              <button type="button" className="create-estimate-btns create-estimate-download-pdf" onClick={handleDownloadPDF} disabled={isSubmitting || loadingEstimate || isDownloadingPDF || isSendingToXero} title="Download PDF">
                {isDownloadingPDF ? (
                  <>
                    <span className="create-estimate-loading">⟳</span>
                    Generating PDF...
                  </>
                ) : (
                  '📄 Generate PDF'
                )}
              </button>
              {/* ✅ XERO BUTTON */}
                {(platform === "xero" || platform === "both") && (
                  <button type="button" className="create-estimate-btns create-estimate-send-xero" onClick={handleSendToXero} disabled={isSubmitting || loadingEstimate || isDownloadingPDF || isSendingToXero} title="Send to Xero">
                    {isSendingToXero ? (
                      <>
                        <span className="create-estimate-loading">⟳</span>
                        Sending to Xero...
                      </>
                    ) : (
                      '📊 Send to Xero'
                    )}
                  </button>
                )}
                {/* ✅ QUICKBOOKS BUTTON */}
                {(platform === "quickbooks" || platform === "both") && (
                  <button type="button" className="create-estimate-btns create-estimate-send-xero" onClick={handleSendToQuickbook} disabled={isSubmitting || loadingEstimate || isDownloadingPDF || isSendingToQuickbook} title="Send to Quickbooks">
                    {isSendingToQuickbook ? (
                      <>
                        <span className="create-estimate-loading">⟳</span>
                        Sending to Quickbooks...
                      </>
                    ) : (
                      '📊 Send to Quickbooks'
                    )}
                  </button>
                )}
              <button type="button" className="create-estimate-btns create-estimate-send-approval" onClick={() => setShowSendApproval(true)} disabled={isSubmitting || loadingEstimate || isDownloadingPDF || isSendingToXero || isSendingApproval || isSendingToClient} title="Send for Approval">
                {isSendingApproval ? (
                  <>
                    <span className="create-estimate-loading">⟳</span>
                    Sending for Approval...
                  </>
                ) : (
                  '📧 Send for Approval'
                )}
              </button>
              <button type="button" className="create-estimate-btns create-estimate-send-client" onClick={() => setShowSendToClient(true)} disabled={isSubmitting || loadingEstimate || isDownloadingPDF || isSendingToXero || isSendingApproval || isSendingToClient} title="Send to Client">
                {isSendingToClient ? (
                  <>
                    <span className="create-estimate-loading">⟳</span>
                    Sending to Client...
                  </>
                ) : (
                  '👤 Send to Client'
                )}
              </button>
            </>
        )}
      </div>

      {pendingDraft && (
          <div className="create-estimate-edit-notice alertBluebar message-bar d-flex align-items-center justify-content-between gap-2">
            <span>
              📝 Unsaved draft found from {new Date(pendingDraft.savedAt).toLocaleString()}.
            </span>
            <span className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-primary" onClick={restoreDraft}>Restore</button>
              <button type="button" className="btn btn-sm btn-light" onClick={discardDraft}>Discard</button>
            </span>
          </div>
        )}

        {hasUnsavedNumber && (
  <div className="create-estimate-error-message alertRedbar message-bar">
    ⚠️ Estimate number changed but not saved. Click "Update Estimate" before sending.
  </div>
)}

      <form onSubmit={handleSubmit} className="create-estimate-form p-4">

        {/* Success message */}
        {successMessage && (
          <div className="create-estimate-success-message alertGreenbar">
            <span className="success-icon">✅</span>
            <span>{successMessage}</span>
          </div>
        )}

        {/* Error message */}
        {apiError && (
          <div className="create-estimate-error-message alertRedbar message-bar">
            <span className="error-icon">⚠️</span>
            <span>{apiError}</span>
          </div>
        )}

        {/* Estimate API Error message */}
        {estimateApiError && (
          <div className="create-estimate-error-message alertRedbar message-bar">
            <span className="error-icon">⚠️</span>
            <span>{estimateApiError}</span>
          </div>
        )}

        {/* Edit mode notice when no data loaded */}
        {isEditMode && !loadingEstimate && !apiError && (
          <div className="create-estimate-edit-notice alertBluebar message-bar">
            <span className="edit-icon fs-6">✏️</span>
            <span>You are editing an existing estimate. Make your changes and click "Update Estimate" to save.</span>
          </div>
        )}
        {/* Estimate Details Section */}
        <div className="create-estimate-section mb-4">
          <h3 className="create-estimate-section-title text-black text-opacity-75 mb-3 pb-1 fw-bold fs-6 border-bottom">Estimate Details</h3>
          <div className="create-estimate-grid">

            <div className="create-estimate-field d-flex flex-column mb-2">
                <label className="fw-bold text-black text-opacity-75 mb-2 p14">Lead Customer Name And ID</label>
                <div className="create-estimate-lead-wrapper">
                <div className="create-estimate-input-wrapper">
                  <span className="create-estimate-icon">👤</span>
                  <input
                    type="text"
                    value={leadSearch}
                    onChange={(e) => handleLeadSearch(e.target.value)}
                    onFocus={() => !selectedLead && leadSearch.length >= 2 && setShowLeadDropdown(true)}
                    className={errors.lead ? 'error' : ''}
                    placeholder={selectedLead ? "Lead selected" : "Select lead"}
                    autoComplete="off"
                    readOnly={!!selectedLead}
                  />
                  {isSearchingLeads && !selectedLead && (
                    <span className="create-estimate-loading">⟳</span>
                  )}
                  {selectedLead && (
                    <button
                      type="button"
                      className="create-estimate-clear-lead"
                      onClick={handleClearLead}
                      title="Clear selected lead"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Lead Dropdown */}
                {showLeadDropdown && !selectedLead && (
                  <div className="create-estimate-lead-dropdown">
                    {apiError ? (
                      <div className="create-estimate-error-item">
                        <span className="error-icon">⚠️</span>
                        <span>{apiError}</span>
                      </div>
                    ) : leadSuggestions.length > 0 ? (
                      leadSuggestions.map((lead) => (
                        <div
                          key={lead.id}
                          className="create-estimate-lead-item"
                          onClick={() => handleLeadSelect(lead)}
                        >
                          <span className="create-estimate-lead-icon">👤</span>
                          <div className="create-estimate-lead-info">
                            <div className="create-estimate-lead-name">{lead.name}</div>
                            {lead.subtitle && (
                              <div className="create-estimate-lead-subtitle">{lead.subtitle}</div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : leadSearch.length >= 2 && !isSearchingLeads ? (
                      <div
                        className="create-estimate-create-lead"
                        onClick={handleCreateNewLead}

                        style={{
                          display: 'none'
                        }}
                      >
                        <span className="create-estimate-create-icon">+</span>
                        <span>Create '{leadSearch}' as a new lead</span>
                      </div>
                    ) : leadSearch.length >= 2 && isSearchingLeads ? (
                      <div className="create-estimate-loading-item">
                        <span className="create-estimate-loading">⟳</span>
                        <span>Searching leads...</span>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              {errors.lead && <span className="error-message">{errors.lead}</span>}
            </div>
            <div className="create-estimate-field d-flex flex-column mb-2">
              <label className="fw-bold text-black text-opacity-75 mb-2 p14">Warranty Period</label>
              <div className="create-estimate-input-wrapper">

                  <span className="create-estimate-icon">⏰</span>

                  <select
                    value={formData.warrantyYear}
                    onChange={(e) => handleInputChange('warrantyYear', e.target.value)}
                    className={errors.warrantyYear ? 'error' : ''}
                  >
                    {warrantyYearOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                {errors.warrantyYear && <span className="error-message">{errors.warrantyYear}</span>}
            </div>


            <div className="create-estimate-field d-flex flex-column mb-2">
              <label className="fw-bold text-black text-opacity-75 mb-2 p14">Category <span className="text-danger">*</span></label>
              <div className="create-estimate-input-wrapper">
                <span className="create-estimate-icon">🏷️</span>
                <select
                  value={formData.categoryId}
                  onChange={(e) => handleInputChange('categoryId', e.target.value)}
                  disabled={loadingCategories}
                >
                  <option value="">
                    {loadingCategories ? 'Loading categories...' : '— Select Category —'}
                  </option>
                  {categories.map(cat => (
                    <option key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.categoryId && <span className="error-message">{errors.categoryId}</span>}
            </div>





            {/* <div className="create-estimate-field d-flex flex-column mb-2 hidden-mobile" style={{visibility: 'hidden'}}>
              <label></label>
              <div className="create-estimate-input-wrapper">
              </div>
            </div> */}

            {/* <div className="create-estimate-field d-flex flex-column mb-2" style={{display: 'none'}}>
              <label className="fw-bold text-black text-opacity-75 mb-2 p14">Issue date</label>
              <div className="create-estimate-input-wrapper">
                <span className="create-estimate-icon">📅</span>
                <input
                  type="date"
                  value={formData.issueDate}
                  onChange={(e) => handleInputChange('issueDate', e.target.value)}
                  className={errors.issueDate ? 'error' : ''}
                />
              </div>
              {errors.issueDate && <span className="error-message">{errors.issueDate}</span>}
            </div> */}

            {/* <div className="create-estimate-field d-flex flex-column mb-2">
              <label className="fw-bold text-black text-opacity-75 mb-2 p14">Expiry date</label>
              <div className="create-estimate-input-wrapper">
                <span className="create-estimate-icon">📅</span>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                />
              </div>
            </div>

            <div className="create-estimate-field d-flex flex-column mb-2">
              <label className="fw-bold text-black text-opacity-75 mb-2 p14">Quote number</label>
              <div className="create-estimate-input-wrapper">
                <input
                  type="text"
                  value={formData.quoteNumber}
                  onChange={(e) => handleInputChange('quoteNumber', e.target.value)}
                  readOnly
                  disabled
                />
              </div>
            </div> */}

          </div>

          {/* Customer address (first column) + Google Maps satellite (top) view in a new tab */}
          {selectedLead && selectedLead.address && (
            <div className="create-estimate-field create-estimate-address-grid">
              <label className="create-estimate-address-label fw-bold text-black text-opacity-75 mb-2 p14">Customer Address</label>
              <div className="create-estimate-input-wrapper create-estimate-address-input">
                <span className="create-estimate-icon">📍</span>
                <input
                  type="text"
                  value={selectedLead.address}
                  readOnly
                  title={selectedLead.address}
                />
              </div>
              <a
                className="create-estimate-map-btn"
                href={`https://maps.google.com/maps?q=${encodeURIComponent(selectedLead.address)}&t=k&z=20`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaMapMarkedAlt aria-hidden="true" />
                <span>View on map</span>
              </a>
            </div>
          )}

          {/* <button type="button" className="create-estimate-add-title">
            + Add a title & summary
          </button> */}
        </div>

        {isEditMode && (
        <div className="create-estimate-section mb-4">
            <div className="create-estimate-grid">
                <div className="create-estimate-field d-flex flex-column mb-2">
                    <label className="fw-bold text-black text-opacity-75 mb-2 p14">Estimate Number</label>
                        <div className="create-estimate-input-wrapper">
                            <input type="text" value={formData.estimationNumber} onChange={(e) => handleInputChange('estimationNumber', e.target.value)} />
                      </div>
                </div>
                <div className="create-estimate-field d-flex flex-column mb-2"></div>
                <div className="create-estimate-field d-flex flex-column mb-2"></div>
            </div>
        </div>
        )}

        {/* Basic Information Section */}
        <div className="create-estimate-section mb-4">
          <h3 className="create-estimate-section-title text-black text-opacity-75 mb-3 pb-1 fw-bold fs-6 border-bottom">Basic Information</h3>

          <div className="create-estimate-basic-info">
            {/* Disclaimer */}
            <div className="create-estimate-field d-flex flex-column mb-2 create-estimate-field-half">
              <label className="fw-bold text-black text-opacity-75 mb-2 p14">Disclaimer</label>
               <textarea id="disclaimer-textarea" value={formData.disclaimer} onChange={handleDisclaimerTextChange} onSelect={handleDisclaimerCursorChange} onKeyUp={handleDisclaimerCursorChange} onMouseUp={handleDisclaimerCursorChange} placeholder="Enter disclaimer text..." rows="8" className="create-estimate-textarea form-control p14" />

              {/* ← Add variables section below the textarea */}
              <div className="create-estimate-variables">
                <label className="create-estimate-variables-label">Insert Variables:</label>
                <div className="create-estimate-variables-grid">
                  {warrantyVariables.map((variable) => (
                    <button
                      key={variable.key}
                      type="button"
                      className="create-estimate-variable-btn"
                      onClick={() => handleInsertDisclaimerVariable(variable)}
                      title={variable.description}
                    >
                      <span className="create-estimate-variable-key">{variable.key}</span>
                      <span className="create-estimate-variable-label">{variable.label}</span>
                    </button>
                  ))}
                </div>
              </div>


              {/* Address Image */}
              <div className="create-estimate-field d-flex flex-column mb-2 create-estimate-field-half mt-4">
                <label className="fw-bold text-black text-opacity-75 mb-2 p14">Address Image</label>
                <div className="create-estimate-file-upload">
                  <input
                    type="file"
                    id="addressImage"
                    accept="image/*"
                    onChange={handleAddressImageChange}
                    className="create-estimate-file-input"
                  />
                  <label htmlFor="addressImage" className="create-estimate-file-label">
                    <span className="create-estimate-icon">📷</span>
                    {formData.addressImage ?
                      (typeof formData.addressImage === 'string' ? 'Address image loaded' : formData.addressImage.name) :
                      'Choose address image'
                    }
                  </label>
                  {formData.addressImage && (
                    <div className="create-estimate-address-image-preview">
                      <img
                        src={typeof formData.addressImage === 'string' ? formData.addressImage : URL.createObjectURL(formData.addressImage)}
                        alt="Address preview"
                        className="create-estimate-address-preview-img"
                      />
                      <button
                        type="button"
                        className="create-estimate-gallery-edit create-estimate-address-image-edit"
                        onClick={() => openPhotoEditor(
                          { type: 'address' },
                          typeof formData.addressImage === 'string'
                            ? { url: formData.addressImage, name: 'address-image.jpg' }
                            : { file: formData.addressImage, name: formData.addressImage.name }
                        )}
                        title="Edit image (rectangles, marks, text)"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="create-estimate-remove-address-image"
                        onClick={() => handleInputChange('addressImage', null)}
                        title="Remove address image"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Warranty */}
            <div className="create-estimate-field d-flex flex-column mb-2 create-estimate-field-half">
              <label className="fw-bold text-black text-opacity-75 mb-2 p14">Warranty</label>
              <div className="quill-editor-container create-estimate-warranty-editor">
                <ReactQuill
                  ref={warrantyQuillRef}
                  theme="snow"
                  value={warrantyEditorHtml(formData.warranty)}
                  onChange={handleWarrantyEditorChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter warranty text..."
                />
              </div>

              {/* Warranty Variables */}
              <div className="create-estimate-variables">
                <label className="create-estimate-variables-label">Insert Variables:</label>
                <div className="create-estimate-variables-grid">
                  {warrantyVariables.map((variable) => (
                    <button
                      key={variable.key}
                      type="button"
                      className="create-estimate-variable-btn"
                      onClick={() => handleInsertVariable(variable)}
                      title={variable.description}
                    >
                      <span className="create-estimate-variable-key">{variable.key}</span>
                      <span className="create-estimate-variable-label">{variable.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Warranty Year Selection */}



          </div>
        </div>

        {/* Items + Exhibits Table Section (drag ⠿ to place exhibits between items) */}
        <div className="create-estimate-section mb-4">


           <div className="create-estimate-table-wrapper">
            <table className="create-estimate-table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '32px' }} />
                <col />
                <col style={{ width: '70px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '110px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ width: '32px', padding: '4px' }}></th>
                  <th>Description</th>
                  <th>Qty.</th>
                  <th>Price</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {orderedBlocks.map((block, index) => {
                  const rowProps = {
                    draggable: dragReadyIndex === index,
                    onDragStart: (e) => handleDragStart(e, index),
                    onDragOver: (e) => handleDragOver(e, index),
                    onDrop: (e) => handleDrop(e, index),
                    onDragEnd: handleDragEnd,
                  };
                  // Blue line shows where the dragged block will land
                  let dropLineClass = '';
                  if (draggedIndex !== null && dragOverIndex === index && draggedIndex !== index) {
                    dropLineClass = draggedIndex < index ? 'drop-line-bottom' : 'drop-line-top';
                  }
                  const draggingClass = `${draggedIndex === index ? 'opacity-50' : ''} ${dropLineClass}`;

                  // Exhibit row: full-width card that can sit between any items
                  if (block.type === 'exhibit') {
                    const exhibit = block.data;
                    return (
                      <tr key={block.key} {...rowProps} className={`exhibit-row ${draggingClass}`}>
                        {renderHandleCell(index)}
                        <td colSpan={4} className="exhibit-row-cell">
                          <ExhibitCard
                            exhibit={exhibit}
                            letter={exhibitLetters[exhibit.id]}
                            setExhibits={setExhibits}
                          />
                        </td>
                      </tr>
                    );
                  }

                  const item = block.data;
                  return (
                  <tr key={block.key} {...rowProps} className={draggingClass}>
                    {renderHandleCell(index, (
                      // item name input kept but hidden, same as original behavior
                      <input
                        type="text"
                        value={item.item}
                        onChange={(e) => handleItemChange(item.id, 'item', e.target.value)}
                        style={{ display: 'none' }}
                      />
                    ))}
                    <td data-label="Description">
                      <div className="create-estimate-description-cell">
                        <div className="create-estimate-description-editor">
                          <div className="quill-editor-container">
                            <ReactQuill
                              ref={(el) => { if (el) quillRefs.current[item.id] = el; }}
                              theme="snow"
                              value={item.description}
                              onChange={handleEditorChange(item.id)}
                              modules={quillModules}
                              formats={quillFormats}
                              placeholder="Enter description..."
                              readOnly={false}
                            />
                          </div>
                          <button
                            type="button"
                            className="create-estimate-add-newbox-btn"
                            onClick={() => handleInsertNewBox(item.id)}
                            title="Insert ----newBox---- and start new line"
                          >
                            + newBox
                          </button>
                        </div>

                        {/* Gallery Images Section */}
                        <div className="create-estimate-gallery-section">
                          <div className="create-estimate-gallery-header">
                            <label className="create-estimate-gallery-label">Gallery Images</label>
                            <input
                              type="file"
                              id={`gallery-${item.id}`}
                              multiple
                              accept="image/*"
                              onChange={(e) => handleGalleryImageAdd(item.id, e.target.files)}
                              className="create-estimate-gallery-input"
                            />
                            <label htmlFor={`gallery-${item.id}`} className="create-estimate-gallery-upload-btn">
                              📷 Add Images
                            </label>
                          </div>

                          {item.galleryImages.length > 0 && (
                            <div className="create-estimate-gallery-preview">
                              {item.galleryImages.map((image, imageIndex) => (
                                // ✅ CHANGED: drag a photo onto another to move it (stopPropagation: the
                                // table row has its own drag & drop for moving whole rows)
                                <div
                                  key={image.id}
                                  className="create-estimate-gallery-item gallery-sortable-item"
                                  draggable
                                  title="Drag to change the order"
                                  onDragStart={(e) => {
                                    e.stopPropagation();
                                    galleryDrag.current = { itemId: item.id, index: imageIndex };
                                    e.dataTransfer.effectAllowed = 'move';
                                    e.dataTransfer.setData('text/plain', 'gallery-photo');
                                  }}
                                  onDragOver={(e) => {
                                    if (galleryDrag.current && galleryDrag.current.itemId === item.id) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }
                                  }}
                                  onDrop={(e) => {
                                    const drag = galleryDrag.current;
                                    if (drag && drag.itemId === item.id) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      moveGalleryImage(item.id, drag.index, imageIndex);
                                    }
                                    galleryDrag.current = null;
                                  }}
                                  onDragEnd={(e) => { e.stopPropagation(); galleryDrag.current = null; }}
                                >
                                  <img
                                    src={image.preview}
                                    alt={image.name}
                                    className="create-estimate-gallery-image"
                                  />
                                  <button
                                    type="button"
                                    className="create-estimate-gallery-edit"
                                    onClick={() => openPhotoEditor({ type: 'gallery', itemId: item.id, imageId: image.id }, { file: image.file, url: image.preview, name: image.name })}
                                    title="Edit image (rectangles, marks, text)"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    type="button"
                                    className="create-estimate-gallery-remove"
                                    onClick={() => handleGalleryImageRemove(item.id, image.id)}
                                    title="Remove image"
                                  >
                                    ✕
                                  </button>
                                  <span className="create-estimate-gallery-name">{image.name}</span>
                                  {/* ✅ NEW: move left / right (easier on a phone than dragging) */}
                                  <div className="gallery-order-btns">
                                    <button type="button" disabled={imageIndex === 0} onClick={() => moveGalleryImage(item.id, imageIndex, imageIndex - 1)} title="Move left">◀</button>
                                    <button type="button" disabled={imageIndex === item.galleryImages.length - 1} onClick={() => moveGalleryImage(item.id, imageIndex, imageIndex + 1)} title="Move right">▶</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <button
                          type="button"
                          className="create-estimate-import-template-btn"
                          onClick={() => handleImportTemplate(item.id)}
                          title="Import template"
                        >
                          📄 Import Template
                        </button>
                      </div>
                    </td>
                    <td data-label="Qty">
                      <input
                        type="number"
                        value={item.qty}
                        onChange={(e) => handleItemChange(item.id, 'qty', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td data-label="Price">
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => handleItemChange(item.id, 'price', e.target.value)}
                        placeholder="0.00 (use negative for discount)"
                        step="0.01"
                      />
                    </td>
                    <td data-label="Amount">
                      <div className="create-estimate-amount-cell">
                        <span>{item.amount || '0.00'}</span>
                        <button
                          type="button"
                          className="create-estimate-remove-btn"
                          onClick={() => removeItem(item.id)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="d-flex gap-2">
            <button type="button" className="create-estimate-add-row" onClick={addItem}>
              Add row
            </button>
            <button type="button" className="create-estimate-add-row" onClick={addExhibit}>
              + Add Exhibit
            </button>
          </div>
          <div className="text-black text-opacity-50 p12 mt-2">
            Tip: drag ⠿ (or use ▲ ▼) to move an exhibit right after the description it belongs to.
          </div>

          {invalidItemsCount > 0 && (
            <div className="create-estimate-error-message alertRedbar message-bar">
              <span className="error-icon">⚠️</span>
              {invalidItemsCount} of the table cells have invalid data entered
            </div>
          )}


        </div>

        {/* Summary Section */}
        <div className="create-estimate-section mb-4">
          <div className="create-estimate-summary">
            <div className="create-estimate-summary-item">
              <span>Subtotal</span>
              <span>{totals.subtotal}</span>
            </div>
            <div className="create-estimate-summary-item">
              <span>Total discount</span>
              <span>{(totals.discount) ? '-' + totals.discount : '0.00'}</span>
            </div>
            <div className="create-estimate-summary-total">
              <span>Total</span>
              <span>{totals.total}</span>
            </div>
          </div>
        </div>

        {/* Terms Section */}
        <div className="create-estimate-section mb-4">
            <h3 className="create-estimate-section-title text-black text-opacity-75 mb-3 pb-1 fw-bold fs-6 border-bottom">Terms</h3>
            <div className="create-estimate-field d-flex flex-column mb-2">
                <textarea value={formData.terms} onChange={(e) => handleInputChange('terms', e.target.value)} placeholder="Add any terms..." rows="6" className="create-estimate-textarea form-control p14" />
            </div>
        </div>

        {/* ✅ NEW: why the last Create / Update failed (stays until next try) */}
        {submitError && (
          <div className="create-estimate-error-message alertRedbar message-bar" role="alert">
            <span className="error-icon">⚠️</span>
            <span>
              {submitError.text} Your work is kept as a draft.
              {/* ✅ NEW: technical detail — please send a screenshot of this line to support */}
              <small className="d-block text-black text-opacity-50 mt-1">Details: {submitError.detail}</small>
            </span>
          </div>
        )}
        {/* ✅ NEW: auto-save problem */}
        {autoSaveError && (
          <div className="create-estimate-error-message alertRedbar message-bar" role="alert">
            <span className="error-icon">⚠️</span>
            <span>{autoSaveError}</span>
          </div>
        )}

        {/* Action Buttons */}
        {/* ✅ CHANGED: added align-items-center */}
        <div className="create-estimate-actions d-flex justify-content-end align-items-center gap-2 pt-3 border-top mt-auto">
          {/* ✅ NEW: "Draft auto-saved at …" */}
          {lastAutoSaved && !autoSaveError && (
            <span className="text-sm text-black text-opacity-50 me-auto">Draft auto-saved at {lastAutoSaved.toLocaleTimeString()}</span>
          )}
          <div className="create-estimate-submit-buttons d-flex gap-2">
            <button type="button" className="create-estimate-cancel btn btn-light text-sm btn-custom px-3 py-2 m-0 " onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="create-estimate-save btn btn-primary text-sm btn-custom px-3 py-2 m-0 shadow-none" disabled={isSubmitting || loadingEstimate}>
              {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Estimate' : 'Create Estimate')}
            </button>
          </div>
        </div>
      </form>

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="create-estimate-template-modal-overlay">
          <div className="create-estimate-template-modal">
            <div className="create-estimate-template-modal-header">
              <h3>Select Template</h3>
              <button
                type="button"
                className="create-estimate-template-close"
                onClick={closeTemplateModal}
              >
                ✕
              </button>
            </div>

            {/* Template Search */}
            <div className="create-estimate-template-search">
              <div className="create-estimate-input-wrapper">
                <span className="create-estimate-icon">🔍</span>
                <input
                  type="text"
                  value={templateSearch}
                  onChange={(e) => handleTemplateSearch(e.target.value)}
                  placeholder="Search templates by name..."
                  className="create-estimate-template-search-input"
                />
                {loadingTemplates && (
                  <span className="create-estimate-loading">⟳</span>
                )}
              </div>
            </div>

            <div className="create-estimate-template-modal-content">
              {loadingTemplates ? (
                <div className="create-estimate-template-loading">
                  <span className="create-estimate-loading">⟳</span>
                  <span>Loading templates...</span>
                </div>
              ) : templates.length > 0 ? (
                <div className="create-estimate-template-list">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="create-estimate-template-item"
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <div className="create-estimate-template-name">
                        {template.template_name}
                      </div>
                      <div
                        className="create-estimate-template-preview"
                        dangerouslySetInnerHTML={{
                          __html: template.template_content ?
                            template.template_content.substring(0, 100) + '...' :
                            'No content'
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="create-estimate-template-empty">
                  <span>
                    {templateSearch.trim() ?
                      `No templates found matching "${templateSearch}"` :
                      'No templates available'
                    }
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Send for Approval Modal */}
      {showSendApproval && (
        <SendApproval
          estimate={editEstimate}
          clientEmail={clientEmail}
          mode="approval"
          approvalSendTo={emailSettings.approval_send_to}
          approvalCc={emailSettings.approval_cc}
          defaultSubject={resolveSubject(emailSettings.approval_subject_template)}

          onClose={() => setShowSendApproval(false)}
          onSuccess={() => {
            setShowSendApproval(false);
            setSuccessMessage('Estimate sent for approval successfully!');
          }}
          onError={(error) => { setEstimateApiError(error); }}
        />
      )}

      {/* Send to Client Modal */}
      {showSendToClient && (
        <SendApproval estimate={editEstimate} clientEmail={clientEmail} clientBcc={emailSettings.client_bcc} mode="client"   defaultSubject={resolveSubject(emailSettings.client_subject_template)}
             onClose={() => setShowSendToClient(false)} onSuccess={() => { setShowSendToClient(false); setSuccessMessage('Estimate sent to client successfully!'); }} onError={(error) => { setEstimateApiError(error); }} />
      )}

      {/* Gallery photo editor */}
      {photoEditor && (
        <PhotoMarkupEditor
          source={photoEditor.source}
          fileName={photoEditor.name}
          onSave={handlePhotoEdited}
          onClose={closePhotoEditor}
        />
      )}
    </div>
  );
};

export default CreateEstimate;
